/**
 * Tier-based feature flag hook.
 *
 * Usage:
 *   const showAIAnalysis = useFeatureFlag('aiAnalysis');
 *   if (!showAIAnalysis) return null;
 *
 * Or bulk:
 *   const flags = useFeatureFlags();
 *   if (!flags.tokenObservability) ...
 */

import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { resolveFlag, resolveAllFlags, type FeatureFlag } from '@/lib/featureFlags';

export function useFeatureFlag(flag: FeatureFlag): boolean {
  const user = useAuthStore((s) => s.user);
  return useMemo(
    () =>
      resolveFlag(flag, {
        role: user?.role,
        tier: user?.tier,
        planLevel: user?.planLevel,
      }),
    [flag, user?.role, user?.tier, user?.planLevel]
  );
}

export function useFeatureFlags(): Record<FeatureFlag, boolean> {
  const user = useAuthStore((s) => s.user);
  return useMemo(
    () =>
      resolveAllFlags({
        role: user?.role,
        tier: user?.tier,
        planLevel: user?.planLevel,
      }),
    [user?.role, user?.tier, user?.planLevel]
  );
}
